if (!customElements.get('floating-video-card')) {
  const TOUCH_PHONE_QUERY = '(max-width: 767px) and (hover: none) and (pointer: coarse)';

  class FloatingVideoCard extends HTMLElement {
    connectedCallback() {
      this.triggers = this.querySelectorAll('.floating-video-card__trigger--media');
      this.copyToggles = this.querySelectorAll('[data-floating-video-collapse-toggle]');
      this.collapseBtn = this.querySelector('[data-floating-video-collapse]');
      this.collapseTargets = this.querySelectorAll('[data-floating-video-collapse-target]');
      this.modal = this.querySelector('[data-floating-video-modal]');
      this.previewVideo = this.querySelector('[data-floating-video-preview]');
      this.modalVideo = this.querySelector('[data-floating-video-modal-video]');
      this.modalYoutube = this.querySelector('[data-floating-video-modal-youtube]');
      this.closeBtn = this.querySelector('[data-floating-video-close]');
      this.backdrop = this.querySelector('[data-floating-video-backdrop]');
      this.sourcesScript = this.querySelector('[data-floating-video-preview-sources]');
      this.dialogPanel = this.modal?.querySelector('[role="dialog"]');
      this.previewWrap = this.querySelector('.floating-video-card__preview-wrap');
      this.previewPoster = this.querySelector('.floating-video-card__preview--poster');

      if (!this.triggers.length) return;

      this.isTouchPhone = window.matchMedia(TOUCH_PHONE_QUERY).matches;
      this.hasModal = Boolean(
        this.modal && this.closeBtn && this.dialogPanel && (this.modalVideo || this.modalYoutube)
      );
      this.focusReturnTrigger = this.triggers[0];

      if (this.hasModal && this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
      }

      this.previewLoaded = false;
      this.boundEscape = this.onDocumentKeydown.bind(this);

      if (this.collapseBtn && this.collapseTargets.length) {
        this.labelCollapse = this.collapseBtn.getAttribute('aria-label') || '';
        this.labelExpand = this.collapseBtn.dataset.labelExpand || '';
      }

      if (this.isTouchPhone) {
        this.enableTouchPhoneMode();
      } else {
        this.bindDesktopCollapse();
      }

      if (this.hasModal) {
        this.triggers.forEach((trigger) => {
          trigger.addEventListener('click', () => this.open());
        });
        this.closeBtn.addEventListener('click', () => this.close());
        this.backdrop.addEventListener('click', () => this.close());

        if (this.modalVideo) {
          this.modalVideo.addEventListener('ended', () => {
            this.modalVideo.pause();
            this.modalVideo.currentTime = 0;
          });
        }
      }

      if (this.previewVideo) {
        this.previewVideo.addEventListener('error', () => this.fallbackPreviewToPoster(), { once: true });
        this.setupPreviewLazyLoad();
      }

      if (!this.isTouchPhone) {
        this.setupPreviewTilt();
      }

      this.setupTrackInset();
    }

    bindDesktopCollapse() {
      if (this.copyToggles.length && this.collapseBtn) {
        this.copyToggles.forEach((toggle) => {
          toggle.addEventListener('click', () => this.toggleCollapsed());
        });
      }

      if (this.collapseBtn && this.collapseTargets.length) {
        this.collapseBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleCollapsed();
        });
      }
    }

    enableTouchPhoneMode() {
      this.classList.add('is-touch-phone');
      this.setCollapsed(true);

      if (!this.hasModal) return;

      const openModal = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.open();
      };

      this.copyToggles.forEach((toggle) => {
        toggle.addEventListener('click', openModal);
      });

      this.addEventListener('click', (event) => {
        if (!this.classList.contains('is-collapsed')) return;
        if (event.target.closest('[data-floating-video-close], [data-floating-video-modal]')) return;
        openModal(event);
      });
    }

    setCollapsed(collapsed) {
      if (!this.collapseBtn || !this.collapseTargets.length) {
        this.classList.toggle('is-collapsed', collapsed);
        return;
      }

      this.classList.toggle('is-collapsed', collapsed);
      this.collapseBtn.setAttribute('aria-expanded', String(!collapsed));
      this.copyToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', String(!collapsed));
      });
      this.collapseTargets.forEach((el) => {
        if (!el.classList.contains('floating-video-card__collapse-target--desc')) return;
        el.toggleAttribute('hidden', collapsed);
      });
      this.syncCollapseLabel(collapsed);

      if (collapsed) {
        this.previewVideo?.pause();
        this.resetPreviewTilt?.();
      } else if (this.previewVideo && !this.previewPosterFallbackApplied) {
        this.previewVideo.play().catch(() => this.fallbackPreviewToPoster());
      }

      this._trackInsetUpdate?.();
    }

    setupTrackInset() {
      const hero = this.closest('.section-hero');
      const content = hero?.querySelector('.section-hero__content');
      if (!hero || !content) return;

      const update = () => {
        const gapRaw = getComputedStyle(document.documentElement).getPropertyValue('--space-sm').trim();
        const gap = parseFloat(gapRaw) || 16;
        const insetBottom = Math.max(0, hero.offsetHeight - content.offsetTop + gap);

        hero.style.setProperty('--hero-floating-card-inset-bottom', `${insetBottom}px`);
      };

      this._trackInsetUpdate = update;
      requestAnimationFrame(update);

      this._trackInsetObserver = new ResizeObserver(update);
      this._trackInsetObserver.observe(content);
      this._trackInsetObserver.observe(hero);

      window.addEventListener('resize', update);

      this._trackInsetCleanup = () => {
        window.removeEventListener('resize', update);
        this._trackInsetObserver?.disconnect();
        hero.style.removeProperty('--hero-floating-card-inset-bottom');
      };
    }

    setupPreviewTilt() {
      if (!this.previewWrap) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (reducedMotion.matches) return;

      const maxTilt = 7;
      let rafId = 0;

      const applyTilt = (x, y) => {
        this.previewWrap.style.setProperty('--tilt-x', `${x}deg`);
        this.previewWrap.style.setProperty('--tilt-y', `${y}deg`);
      };

      const resetTilt = () => {
        if (rafId) cancelAnimationFrame(rafId);
        this.previewWrap.classList.remove('is-tilting');
        applyTilt(0, 0);
      };

      const onMove = (event) => {
        if (this.classList.contains('is-collapsed')) return;
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          const rect = this.previewWrap.getBoundingClientRect();
          const px = (event.clientX - rect.left) / rect.width - 0.5;
          const py = (event.clientY - rect.top) / rect.height - 0.5;
          applyTilt(px * maxTilt * 2, -py * maxTilt * 2);
        });
      };

      const onEnter = () => {
        if (this.classList.contains('is-collapsed')) return;
        this.previewWrap.classList.add('is-tilting');
      };

      const onLeave = () => {
        resetTilt();
      };

      this.previewWrap.addEventListener('mouseenter', onEnter);
      this.previewWrap.addEventListener('mousemove', onMove);
      this.previewWrap.addEventListener('mouseleave', onLeave);

      this.boundTiltCleanup = () => {
        this.previewWrap.removeEventListener('mouseenter', onEnter);
        this.previewWrap.removeEventListener('mousemove', onMove);
        this.previewWrap.removeEventListener('mouseleave', onLeave);
        resetTilt();
      };

      this.resetPreviewTilt = resetTilt;

      reducedMotion.addEventListener('change', (event) => {
        if (event.matches) {
          resetTilt();
          this.boundTiltCleanup?.();
          this.boundTiltCleanup = null;
        }
      });
    }

    syncCollapseLabel(collapsed) {
      if (!this.collapseBtn) return;
      if (collapsed) {
        if (this.labelExpand) this.collapseBtn.setAttribute('aria-label', this.labelExpand);
      } else if (this.labelCollapse) {
        this.collapseBtn.setAttribute('aria-label', this.labelCollapse);
      }
    }

    toggleCollapsed() {
      if (!this.collapseBtn || !this.collapseTargets.length || this.isTouchPhone) return;
      this.setCollapsed(!this.classList.contains('is-collapsed'));
    }

    fallbackPreviewToPoster() {
      if (this.previewPosterFallbackApplied || !this.previewPoster) return;
      this.previewPosterFallbackApplied = true;
      this.classList.add('is-preview-poster-fallback');
      this.previewVideo?.pause();
    }

    setupPreviewLazyLoad() {
      if (!this.previewVideo) return;
      if (!this.sourcesScript || !this.sourcesScript.textContent.trim()) {
        this.fallbackPreviewToPoster();
        return;
      }

      let sources;
      try {
        sources = JSON.parse(this.sourcesScript.textContent);
      } catch {
        this.fallbackPreviewToPoster();
        return;
      }

      if (!Array.isArray(sources) || !sources.length) {
        this.fallbackPreviewToPoster();
        return;
      }

      const loadPreview = () => {
        if (this.previewLoaded) return;
        this.previewLoaded = true;
        sources.forEach((s) => {
          const source = document.createElement('source');
          source.src = s.url;
          source.type = s.mime_type;
          this.previewVideo.appendChild(source);
        });
        this.previewVideo.load();
        this.previewVideo.play().catch(() => this.fallbackPreviewToPoster());
      };

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            io.disconnect();
            loadPreview();
          },
          { rootMargin: '80px' }
        );
        io.observe(this.previewVideo);
      } else {
        loadPreview();
      }
    }

    playModalMedia() {
      if (this.modalVideo) {
        this.modalVideo.play().catch(() => {});
        return;
      }

      if (!this.modalYoutube) return;

      const embedSrc = this.modalYoutube.dataset.embedSrc;
      if (!embedSrc) return;

      const separator = embedSrc.includes('?') ? '&' : '?';
      this.modalYoutube.src = `${embedSrc}${separator}autoplay=1`;
    }

    pauseModalMedia() {
      if (this.modalVideo) {
        this.modalVideo.pause();
        this.modalVideo.currentTime = 0;
        return;
      }

      if (this.modalYoutube) {
        this.modalYoutube.src = '';
      }
    }

    open() {
      if (!this.hasModal) return;
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'true'));
      this.modal.removeAttribute('hidden');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.boundEscape);
      trapFocus(this.dialogPanel, this.closeBtn);
      this.playModalMedia();
    }

    close() {
      if (!this.hasModal) return;
      this.pauseModalMedia();
      this.modal.setAttribute('hidden', '');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.boundEscape);
      removeTrapFocus(this.focusReturnTrigger);
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    }

    onDocumentKeydown(event) {
      if (event.code.toUpperCase() !== 'ESCAPE') return;
      event.preventDefault();
      this.close();
    }

    disconnectedCallback() {
      if (this.boundEscape) {
        document.removeEventListener('keydown', this.boundEscape);
      }
      this._trackInsetCleanup?.();
      this.boundTiltCleanup?.();
      const wasOpen = this.hasModal && this.modal && !this.modal.hasAttribute('hidden');
      if (wasOpen) {
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus();
      }
      if (this.modal?.parentElement === document.body) {
        this.modal.remove();
      }
    }
  }

  customElements.define('floating-video-card', FloatingVideoCard);
}
